import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider, NotificationNavigator } from './context/NotificationContext';

// Pages
import UserHome from './pages/User/UserHome';
import RestaurantDetails from './pages/User/RestaurantDetails';
import Cart from './pages/User/Cart';
import MyOrders from './pages/User/MyOrders';
import OrderTracking from './pages/User/OrderTracking';
import Profile from './pages/User/Profile';

import RestaurantDashboard from './pages/Restaurant/Dashboard';
import RestaurantOnboarding from './pages/Restaurant/RestaurantOnboarding';
import PlanSelection from './pages/Restaurant/PlanSelection';
import SubscriptionPayment from './pages/Restaurant/SubscriptionPayment';
import PaymentVerification from './pages/Restaurant/PaymentVerification';
import MenuManagement from './pages/Restaurant/MenuManagement';
import RestaurantOrders from './pages/Restaurant/Orders';
import RestaurantProfile from './pages/Restaurant/Profile';
import CouponManagement from './pages/Restaurant/CouponManagement';
import OpeningHours from './pages/Restaurant/OpeningHours';
import Reviews from './pages/Restaurant/Reviews';
import Earnings from './pages/Restaurant/Earnings';
import SubscriptionInfo from './pages/Restaurant/SubscriptionInfo';
import AddonSubscriptions from './pages/Restaurant/AddonSubscriptions';
import AddonPaymentVerification from './pages/Restaurant/AddonPaymentVerification';
import RestaurantHelpSupport from './pages/Restaurant/HelpSupport';
import NotificationSettings from './pages/Restaurant/NotificationSettings';
import PrinterSettings from './pages/Restaurant/PrinterSettings';
import SalesReport from './pages/Restaurant/SalesReport';
import AboutPage from './pages/Restaurant/About';

import DeliveryDashboard from './pages/Delivery/Dashboard';
import AvailableOrders from './pages/Delivery/AvailableOrders';
import MyDeliveries from './pages/Delivery/MyDeliveries';
import CODSettlement from './pages/Delivery/CODSettlement';
import CODSettlementVerification from './pages/Delivery/CODSettlementVerification';
import DeliveryProfile from './pages/Delivery/Profile';
import LocationUpdate from './pages/DeliveryPartner/LocationUpdate';
import DeliveryPartnerOnboarding from './pages/DeliveryPartner/DeliveryPartnerOnboarding';
import DeliveryPaymentVerification from './pages/DeliveryPartner/DeliveryPaymentVerification';

import AdminDashboard from './pages/Admin/Dashboard';
import AdminOrders from './pages/Admin/Orders';
import Approvals from './pages/Admin/Approvals';
import Performance from './pages/Admin/Performance';
import RestaurantReviews from './pages/Admin/RestaurantReviews';
import RestaurantManagement from './pages/Admin/RestaurantManagement';
import SubscriptionManagement from './pages/Admin/SubscriptionManagement';
import CityManagement from './pages/Admin/CityManagement';
import WorkAreaManagement from './pages/Admin/WorkAreaManagement';
import Settings from './pages/Admin/Settings';
import AccountManagement from './pages/Admin/AccountManagement';
import AdminSupportTickets from './pages/Admin/SupportTickets';
import SupportTeamManagement from './pages/Admin/SupportTeamManagement';
import PayoutManagement from './pages/Admin/PayoutManagement';
import DeliveryFeeSettings from './pages/Admin/DeliveryFeeSettings';
import DeliveryDistanceReport from './pages/Admin/DeliveryDistanceReport';
import SlotManagement from './pages/Admin/SlotManagement';
import GigBuilder from './pages/Admin/GigBuilder';
import Notifications from './pages/Admin/Notifications';
import AdminCouponManagement from './pages/Admin/CouponManagement';
import AddonManagement from './pages/Admin/AddonManagement';
import BannerSlideManagement from './pages/Admin/BannerSlideManagement';
import HomePriorityManagement from './pages/Admin/HomePriorityManagement';
import ChangeRequestManagement from './pages/Admin/ChangeRequestManagement';
import DailySocialPost from './pages/Admin/DailySocialPost';
import PrintSettingsManagement from './pages/Admin/PrintSettingsManagement';
import AttenderManagement from './pages/Admin/AttenderManagement';
import CompetitorIntel from './pages/Admin/CompetitorIntel';
import ZomatoCalculator from './pages/Admin/ZomatoCalculator';
import ZomatoPayoutAdmin from './pages/Admin/ZomatoPayout';
import RestaurantZomatoPayout from './pages/Restaurant/ZomatoPayout';
import PriorityZones from './pages/Admin/PriorityZones';
import LiveDeliveryMap from './pages/Admin/LiveDeliveryMap';
import DeliveryAssignmentTracker from './pages/Admin/DeliveryAssignmentTracker';
import DeliveryPartnerManagement from './pages/Admin/DeliveryPartnerManagement';
import DeliveryPartnerWorkAnalysis from './pages/Admin/DeliveryPartnerWorkAnalysis';
import SurgeAnalytics from './pages/Admin/SurgeAnalytics';
import MilestoneManagement from './pages/Admin/MilestoneManagement';
import ReferralTeamManagement from './pages/Admin/ReferralTeamManagement';
import AdminRestaurantManager from './pages/Admin/AdminRestaurantManager';
import ImpersonationBanner from './components/ImpersonationBanner';
import ManualAdjustments from './pages/Admin/ManualAdjustments';
import InfrastructureMonitor from './pages/Admin/InfrastructureMonitor';
import ServerMetrics from './pages/Admin/ServerMetrics';
import RateLimitManagement from './pages/Admin/RateLimitManagement';
import PlatformSettings from './pages/Admin/PlatformSettings';
import CustomerPlatformFeeSettings from './pages/Admin/CustomerPlatformFeeSettings';
import InvoiceManagement from './pages/Admin/InvoiceManagement';
import FinanceAuditManagement from './pages/Admin/FinanceAuditManagement';
import FinanceHub from './pages/Admin/FinanceHub';
import GrowthDiagnostics from './pages/Admin/GrowthDiagnostics';
import UserInsights from './pages/Admin/UserInsights';
import CartRecovery from './pages/Admin/CartRecovery';
import ReEngagement from './pages/Admin/ReEngagement';
import AiMessaging from './pages/Admin/AiMessaging';
import MithaiSpecial from './pages/Admin/MithaiSpecial';
import LocalFamousKarts from './pages/Admin/LocalFamousKarts';
import DeliveryServiceSettings from './pages/Admin/DeliveryServiceSettings';
import Refunds from './pages/Admin/Refunds';
import BillingModels from './pages/Admin/BillingModels';
import WalletSettings from './pages/Admin/WalletSettings';
import OtpSettings from './pages/Admin/OtpSettings';
import WalletAnalytics from './pages/Admin/WalletAnalytics';
import SalesExport from './pages/Admin/SalesExport';
import ContactsExport from './pages/Admin/ContactsExport';
import ItemDiscounts from './pages/Admin/ItemDiscounts';
import CAAuditReports from './pages/Admin/CAAuditReports';
import AppVersionSettings from './pages/Admin/AppVersionSettings';
import PaymentSettings from './pages/Admin/PaymentSettings';
import CodAutopay from './pages/Admin/CodAutopay';
import CodSettlement from './pages/Admin/CodSettlement';
import NotificationSoundSettings from './pages/Admin/NotificationSoundSettings';
import HelpSupport from './pages/HelpSupport';
import RestaurantInvoices from './pages/Restaurant/Invoices';
import BankDetails from './pages/Restaurant/BankDetails';
import RestaurantLayout from './components/RestaurantLayout';
import ErrorBoundary from './components/ErrorBoundary';

import SupportTeamLogin from './pages/SupportTeam/SupportTeamLogin';
import RestaurantManagerLogin from './pages/RestaurantManager/Login';
import RestaurantManagerSelect from './pages/RestaurantManager/Select';
import RestaurantManagers from './pages/Admin/RestaurantManagers';
import PersonalFinanceHub from './pages/Admin/PersonalFinanceHub';
import SupportTeamDashboard from './pages/SupportTeam/Dashboard';
import SupportRestaurantManager from './pages/SupportTeam/RestaurantManager';

// OTP Authentication Pages
import PhoneLogin from './pages/Auth/PhoneLogin';
import CompleteProfile from './pages/Auth/CompleteProfile';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
};

// Delivery Partner Protected Route - Requires Approval
const DeliveryProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.role !== 'delivery') {
    return <Navigate to="/" />;
  }

  // Check if delivery partner onboarding is approved
  const deliveryPartner = user.deliveryPartner || {};
  const isApproved = deliveryPartner.onboardingStatus === 'approved';

  if (!isApproved) {
    return <Navigate to="/delivery/onboarding" />;
  }

  return children;
};

// Restaurant Protected Route - Requires Active Subscription
const RestaurantProtectedRoute = ({ children }) => {
  const { user, loading, restaurant, requiresSubscriptionAction, isFirstTimeSubscription } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.role !== 'restaurant') {
    return <Navigate to="/" />;
  }

  // Check if restaurant exists and needs onboarding
  if (!restaurant || !restaurant.onboardingStatus?.contractSigned) {
    return <Navigate to="/restaurant/onboarding" />;
  }

  // Check if restaurant is approved but needs subscription
  if (requiresSubscriptionAction) {
    // Pass isRenewal state through URL parameter
    const isRenewal = !isFirstTimeSubscription;
    return <Navigate to={`/restaurant/plan-selection?renewal=${isRenewal}`} />;
  }

  return <RestaurantLayout>{children}</RestaurantLayout>;
};

// Role-based Home Redirect
const Home = () => {
  const { user, restaurant, requiresSubscriptionAction, isFirstTimeSubscription } = useAuth();

  if (!user) return <Navigate to="/login" />;

  // Check if profile needs completion
  if (!user.profileCompleted || !user.name) {
    return <Navigate to="/auth/complete-profile" />;
  }

  switch (user.role) {
    case 'user':
      return <Navigate to="/user/home" />;
    case 'restaurant':
      // Check if restaurant needs onboarding
      if (!restaurant || !restaurant.onboardingStatus?.contractSigned) {
        return <Navigate to="/restaurant/onboarding" />;
      }
      // Check if restaurant needs subscription
      if (requiresSubscriptionAction) {
        const isRenewal = !isFirstTimeSubscription;
        return <Navigate to={`/restaurant/plan-selection?renewal=${isRenewal}`} />;
      }
      return <Navigate to="/restaurant/dashboard" />;
    case 'delivery':
      // Check if delivery partner has completed onboarding
      const deliveryPartner = user.deliveryPartner || {};
      const hasCompletedOnboarding = deliveryPartner.onboardingStatus === 'approved';

      if (!hasCompletedOnboarding) {
        return <Navigate to="/delivery/onboarding" />;
      }
      return <Navigate to="/delivery/dashboard" />;
    case 'admin':
      return <Navigate to="/admin/dashboard" />;
    default:
      return <Navigate to="/login" />;
  }
};

function App() {
  return (
    // Root-level error boundary — catches render errors anywhere below.
    // Without this, a single buggy component can blank the whole SPA.
    <ErrorBoundary>
    <AuthProvider>
      <Router>
        <NotificationProvider>
          <NotificationNavigator>
            <ImpersonationBanner />
            <Routes>
          {/* OTP Authentication Routes */}
          <Route path="/login" element={<PhoneLogin />} />
          <Route path="/register" element={<PhoneLogin />} />
          <Route path="/register-restaurant" element={<PhoneLogin />} />
          <Route path="/register-delivery" element={<PhoneLogin />} />
          {/* Note: /become-rider is served as static HTML by nginx */}
          <Route path="/auth/complete-profile" element={
            <ProtectedRoute allowedRoles={['user', 'restaurant', 'delivery', 'admin', 'support']}>
              <CompleteProfile />
            </ProtectedRoute>
          } />

          {/* Home redirect for authenticated users entering the app */}
          <Route path="/" element={<Home />} />

          {/* User Routes */}
          <Route path="/user/home" element={
            <ProtectedRoute allowedRoles={['user']}>
              <UserHome />
            </ProtectedRoute>
          } />
          <Route path="/user/restaurant/:id" element={
            <ProtectedRoute allowedRoles={['user']}>
              <RestaurantDetails />
            </ProtectedRoute>
          } />
          <Route path="/user/cart" element={
            <ProtectedRoute allowedRoles={['user']}>
              <Cart />
            </ProtectedRoute>
          } />
          <Route path="/user/orders" element={
            <ProtectedRoute allowedRoles={['user']}>
              <MyOrders />
            </ProtectedRoute>
          } />
          <Route path="/user/order/:id" element={
            <ProtectedRoute allowedRoles={['user']}>
              <OrderTracking />
            </ProtectedRoute>
          } />
          <Route path="/user/profile" element={
            <ProtectedRoute allowedRoles={['user']}>
              <Profile />
            </ProtectedRoute>
          } />

          {/* Restaurant Routes */}
          <Route path="/restaurant/register" element={
            <ProtectedRoute allowedRoles={['restaurant']}>
              <RestaurantOnboarding />
            </ProtectedRoute>
          } />
          <Route path="/restaurant/onboarding" element={
            <ProtectedRoute allowedRoles={['restaurant']}>
              <RestaurantOnboarding />
            </ProtectedRoute>
          } />
          <Route path="/restaurant/plan-selection" element={
            <ProtectedRoute allowedRoles={['restaurant']}>
              <PlanSelection />
            </ProtectedRoute>
          } />
          <Route path="/restaurant/subscription-payment" element={
            <ProtectedRoute allowedRoles={['restaurant']}>
              <SubscriptionPayment />
            </ProtectedRoute>
          } />
          <Route path="/restaurant/payment-verification" element={
            <ProtectedRoute allowedRoles={['restaurant']}>
              <PaymentVerification />
            </ProtectedRoute>
          } />
          <Route path="/restaurant/dashboard" element={
            <RestaurantProtectedRoute>
              <RestaurantDashboard />
            </RestaurantProtectedRoute>
          } />
          <Route path="/restaurant/menu" element={
            <RestaurantProtectedRoute>
              <MenuManagement />
            </RestaurantProtectedRoute>
          } />
          <Route path="/restaurant/orders" element={
            <RestaurantProtectedRoute>
              <RestaurantOrders />
            </RestaurantProtectedRoute>
          } />
          <Route path="/restaurant/profile" element={
            <RestaurantProtectedRoute>
              <RestaurantProfile />
            </RestaurantProtectedRoute>
          } />
          <Route path="/restaurant/coupons" element={
            <RestaurantProtectedRoute>
              <CouponManagement />
            </RestaurantProtectedRoute>
          } />
          <Route path="/restaurant/tomato-calculator" element={
            <RestaurantProtectedRoute>
              <RestaurantZomatoPayout />
            </RestaurantProtectedRoute>
          } />
          <Route path="/restaurant/opening-hours" element={
            <RestaurantProtectedRoute>
              <OpeningHours />
            </RestaurantProtectedRoute>
          } />
          <Route path="/restaurant/reviews" element={
            <RestaurantProtectedRoute>
              <Reviews />
            </RestaurantProtectedRoute>
          } />
          <Route path="/restaurant/earnings" element={
            <RestaurantProtectedRoute>
              <Earnings />
            </RestaurantProtectedRoute>
          } />
          <Route path="/restaurant/subscription-info" element={
            <RestaurantProtectedRoute>
              <SubscriptionInfo />
            </RestaurantProtectedRoute>
          } />
          <Route path="/restaurant/addon-subscriptions" element={
            <RestaurantProtectedRoute>
              <AddonSubscriptions />
            </RestaurantProtectedRoute>
          } />
          <Route path="/restaurant/addon-payment-verification" element={
            <ProtectedRoute allowedRoles={['restaurant']}>
              <AddonPaymentVerification />
            </ProtectedRoute>
          } />
          <Route path="/restaurant/help-support" element={
            <RestaurantProtectedRoute>
              <RestaurantHelpSupport />
            </RestaurantProtectedRoute>
          } />
          <Route path="/restaurant/notification-settings" element={
            <RestaurantProtectedRoute>
              <NotificationSettings />
            </RestaurantProtectedRoute>
          } />
          <Route path="/restaurant/printer-settings" element={
            <RestaurantProtectedRoute>
              <PrinterSettings />
            </RestaurantProtectedRoute>
          } />
          <Route path="/restaurant/sales-report" element={
            <RestaurantProtectedRoute>
              <SalesReport />
            </RestaurantProtectedRoute>
          } />
          <Route path="/restaurant/about" element={
            <RestaurantProtectedRoute>
              <AboutPage />
            </RestaurantProtectedRoute>
          } />
          <Route path="/restaurant/invoices" element={
            <RestaurantProtectedRoute>
              <RestaurantInvoices />
            </RestaurantProtectedRoute>
          } />
          <Route path="/restaurant/bank-details" element={
            <RestaurantProtectedRoute>
              <BankDetails />
            </RestaurantProtectedRoute>
          } />

          {/* Delivery Routes */}
          {/* Onboarding routes - NO approval required */}
          <Route path="/delivery/onboarding" element={
            <ProtectedRoute allowedRoles={['delivery']}>
              <DeliveryPartnerOnboarding />
            </ProtectedRoute>
          } />
          <Route path="/delivery/payment-verification" element={
            <ProtectedRoute allowedRoles={['delivery']}>
              <DeliveryPaymentVerification />
            </ProtectedRoute>
          } />

          {/* Operational routes - REQUIRE approval */}
          <Route path="/delivery/dashboard" element={
            <DeliveryProtectedRoute>
              <DeliveryDashboard />
            </DeliveryProtectedRoute>
          } />
          <Route path="/delivery/available" element={
            <DeliveryProtectedRoute>
              <AvailableOrders />
            </DeliveryProtectedRoute>
          } />
          <Route path="/delivery/my-deliveries" element={
            <DeliveryProtectedRoute>
              <MyDeliveries />
            </DeliveryProtectedRoute>
          } />
          <Route path="/delivery/settlement" element={
            <DeliveryProtectedRoute>
              <CODSettlement />
            </DeliveryProtectedRoute>
          } />
          <Route path="/delivery/cod-settlement-verification" element={
            <ProtectedRoute allowedRoles={['delivery']}>
              <CODSettlementVerification />
            </ProtectedRoute>
          } />
          <Route path="/delivery/location" element={
            <DeliveryProtectedRoute>
              <LocationUpdate />
            </DeliveryProtectedRoute>
          } />
          <Route path="/delivery/profile" element={
            <DeliveryProtectedRoute>
              <DeliveryProfile />
            </DeliveryProtectedRoute>
          } />

          {/* Admin Routes */}
          <Route path="/admin" element={<Navigate to="/admin/dashboard" />} />
          <Route path="/admin/dashboard" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/orders" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminOrders />
            </ProtectedRoute>
          } />
          <Route path="/admin/approvals" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Approvals />
            </ProtectedRoute>
          } />
          <Route path="/admin/delivery-partners" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DeliveryPartnerManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/delivery-work-analysis" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DeliveryPartnerWorkAnalysis />
            </ProtectedRoute>
          } />
          <Route path="/admin/performance" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Performance />
            </ProtectedRoute>
          } />
          <Route path="/admin/restaurant-reviews" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <RestaurantReviews />
            </ProtectedRoute>
          } />
          <Route path="/admin/restaurants" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <RestaurantManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/subscriptions" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <SubscriptionManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/cities" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <CityManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/work-areas" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <WorkAreaManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Settings />
            </ProtectedRoute>
          } />
          <Route path="/admin/account-management" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AccountManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/support-tickets" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminSupportTickets />
            </ProtectedRoute>
          } />
          <Route path="/admin/support-team" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <SupportTeamManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/restaurant-managers" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <RestaurantManagers />
            </ProtectedRoute>
          } />
          <Route path="/admin/personal-finance-hub" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PersonalFinanceHub />
            </ProtectedRoute>
          } />
          <Route path="/admin/payouts" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PayoutManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/delivery-fee-settings" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DeliveryFeeSettings />
            </ProtectedRoute>
          } />
          <Route path="/admin/delivery-distance" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DeliveryDistanceReport />
            </ProtectedRoute>
          } />
          <Route path="/admin/milestones" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MilestoneManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/referral-teams" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ReferralTeamManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/restaurant-manager" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminRestaurantManager />
            </ProtectedRoute>
          } />
          <Route path="/admin/adjustments" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ManualAdjustments />
            </ProtectedRoute>
          } />
          <Route path="/admin/slots" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <SlotManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/gig-builder" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <GigBuilder />
            </ProtectedRoute>
          } />
          <Route path="/admin/notifications" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Notifications />
            </ProtectedRoute>
          } />
          <Route path="/admin/coupons" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminCouponManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/addons" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AddonManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/banner-slides" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <BannerSlideManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/home-priority" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <HomePriorityManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/change-requests" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ChangeRequestManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/daily-post" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DailySocialPost />
            </ProtectedRoute>
          } />
          <Route path="/admin/print-settings" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PrintSettingsManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/attender" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AttenderManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/competitor-intel" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <CompetitorIntel />
            </ProtectedRoute>
          } />
          <Route path="/admin/vs-tomato" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ZomatoCalculator />
            </ProtectedRoute>
          } />
          <Route path="/admin/tomato-payout" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ZomatoPayoutAdmin />
            </ProtectedRoute>
          } />
          <Route path="/admin/priority-zones" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PriorityZones />
            </ProtectedRoute>
          } />
          <Route path="/admin/live-map" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <LiveDeliveryMap />
            </ProtectedRoute>
          } />
          <Route path="/admin/assignment-tracker" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DeliveryAssignmentTracker />
            </ProtectedRoute>
          } />
          <Route path="/admin/surge-analytics" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <SurgeAnalytics />
            </ProtectedRoute>
          } />
          <Route path="/admin/infrastructure" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <InfrastructureMonitor />
            </ProtectedRoute>
          } />
          <Route path="/admin/server-metrics" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ServerMetrics />
            </ProtectedRoute>
          } />
          <Route path="/admin/rate-limits" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <RateLimitManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/platform-settings" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PlatformSettings />
            </ProtectedRoute>
          } />
          <Route path="/admin/customer-platform-fee" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <CustomerPlatformFeeSettings />
            </ProtectedRoute>
          } />
          <Route path="/admin/invoices" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <InvoiceManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/finance-audit" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <FinanceAuditManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/finance-hub" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <FinanceHub />
            </ProtectedRoute>
          } />
          <Route path="/admin/growth-diagnostics" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <GrowthDiagnostics />
            </ProtectedRoute>
          } />
          <Route path="/admin/user-insights" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <UserInsights />
            </ProtectedRoute>
          } />
          <Route path="/admin/cart-recovery" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <CartRecovery />
            </ProtectedRoute>
          } />
          <Route path="/admin/re-engagement" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ReEngagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/ai-messaging" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AiMessaging />
            </ProtectedRoute>
          } />
          <Route path="/admin/daily-nudge" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AiMessaging />
            </ProtectedRoute>
          } />
          <Route path="/admin/mithai-special" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MithaiSpecial />
            </ProtectedRoute>
          } />
          <Route path="/admin/local-famous-karts" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <LocalFamousKarts />
            </ProtectedRoute>
          } />
          <Route path="/admin/delivery-service" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DeliveryServiceSettings />
            </ProtectedRoute>
          } />
          <Route path="/admin/refunds" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Refunds />
            </ProtectedRoute>
          } />
          <Route path="/admin/billing-models" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <BillingModels />
            </ProtectedRoute>
          } />
          <Route path="/admin/wallet-settings" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <WalletSettings />
            </ProtectedRoute>
          } />
          <Route path="/admin/otp-provider" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <OtpSettings />
            </ProtectedRoute>
          } />
          <Route path="/admin/wallet-analytics" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <WalletAnalytics />
            </ProtectedRoute>
          } />
          <Route path="/admin/sales-export" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <SalesExport />
            </ProtectedRoute>
          } />
          <Route path="/admin/contacts-export" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ContactsExport />
            </ProtectedRoute>
          } />
          <Route path="/admin/item-discounts" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ItemDiscounts />
            </ProtectedRoute>
          } />
          <Route path="/admin/ca-audit-reports" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <CAAuditReports />
            </ProtectedRoute>
          } />
          <Route path="/admin/app-versions" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AppVersionSettings />
            </ProtectedRoute>
          } />
          <Route path="/admin/payment-settings" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PaymentSettings />
            </ProtectedRoute>
          } />
          <Route path="/admin/cod-autopay" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <CodAutopay />
            </ProtectedRoute>
          } />
          <Route path="/admin/cod-settlement" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <CodSettlement />
            </ProtectedRoute>
          } />
          <Route path="/admin/notification-sounds" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <NotificationSoundSettings />
            </ProtectedRoute>
          } />

          {/* Help & Support - Available to all authenticated users */}
          <Route path="/help" element={
            <ProtectedRoute allowedRoles={['user', 'restaurant', 'delivery', 'admin']}>
              <HelpSupport />
            </ProtectedRoute>
          } />

          {/* Support Team Routes */}
          <Route path="/support-team/login" element={<SupportTeamLogin />} />
          <Route path="/restaurant-manager/login" element={<RestaurantManagerLogin />} />
          {/* Self-guards inside the component (restores the manager session on
              entry) so browser-BACK from an opened outlet returns here instead of
              being bounced by a role check on the now owner-scoped token. */}
          <Route path="/restaurant-manager/select" element={<RestaurantManagerSelect />} />
          <Route path="/support-team/dashboard" element={
            <ProtectedRoute allowedRoles={['support']}>
              <SupportTeamDashboard />
            </ProtectedRoute>
          } />
          <Route path="/support-team/restaurant-manager" element={
            <ProtectedRoute allowedRoles={['support']}>
              <SupportRestaurantManager />
            </ProtectedRoute>
          } />
          {/* Support team gets full Orders + Live Map access (same admin pages,
              role-aware to hit the /api/support/* endpoints). */}
          <Route path="/support-team/orders" element={
            <ProtectedRoute allowedRoles={['support']}>
              <AdminOrders />
            </ProtectedRoute>
          } />
          <Route path="/support-team/live-map" element={
            <ProtectedRoute allowedRoles={['support']}>
              <LiveDeliveryMap />
            </ProtectedRoute>
          } />
            </Routes>
          </NotificationNavigator>
        </NotificationProvider>
      </Router>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
